import { useState, useEffect } from 'react';

// mui
import {
    Slide,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    IconButton,
    Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// icons
import {
    ChevronRight as ChevronRightIcon,
    ArrowBack as ArrowBackIcon,
    Logout as LogoutIcon,
    LogoutOutlined as LogoutOutlinedIcon,
    Password as PasswordIcon,
    PasswordOutlined as PasswordOutlinedIcon,
    Lock as LockIcon,
    LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';

// components
import { SettingAccountChangePassword, SettingsAccountMfa } from '../';

// api
import { Logout } from '../../api';

function SettingAccount({ onSubViewChange, onBack }) {
    const [selectedTab, setSelectedTab] = useState(null);
    const [hoveredTab, setHoveredTab] = useState(null);

    const onClickLogout = () => {
        Logout();
    };

    const onClickTab = (tab) => {
        if (tab.action) {
            tab.action();
        } else {
            setSelectedTab(tab.value);
        }
    };

    const handleBack = () => {
        setSelectedTab(null);
    };

    const settingTabs = [
        {
            value: '1',
            label: 'Two-Factor Authentication',
            icon: LockOutlinedIcon,
            selectedIcon: LockIcon,
            body: <SettingsAccountMfa onBack={handleBack} />,
            action: null,
        },
        {
            value: '2',
            label: 'Change Password',
            icon: PasswordOutlinedIcon,
            selectedIcon: PasswordIcon,
            body: <SettingAccountChangePassword onCancel={handleBack} />,
            action: null,
        },
        {
            value: '3',
            label: 'Logout',
            icon: LogoutOutlinedIcon,
            selectedIcon: LogoutIcon,
            body: null,
            action: onClickLogout,
        },
    ];

    const currentTab = settingTabs.find((tab) => tab.value === selectedTab);

    useEffect(() => {
        onSubViewChange?.(!!selectedTab);
        return () => onSubViewChange?.(false);
    }, [selectedTab, onSubViewChange]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            onBack?.();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onBack]);

    return (
        <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
            <Slide direction='right' in={!selectedTab} timeout={300} mountOnEnter unmountOnExit>
                <Box>
                    <List sx={{ width: '100%', pt: 0 }}>
                        {settingTabs.map((tab, index) => {
                            const isSelected = selectedTab === tab.value;
                            const isHovered = hoveredTab === tab.value;
                            const IconComponent = isSelected || isHovered ? tab.selectedIcon : tab.icon;
                            return (
                                <Box key={tab.value}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => onClickTab(tab)}
                                            onMouseEnter={() => setHoveredTab(tab.value)}
                                            onMouseLeave={() => setHoveredTab(null)}>
                                            <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                                                <IconComponent />
                                            </ListItemIcon>
                                            <ListItemText primary={tab.label} sx={{ color: 'text.primary' }} />
                                            {tab.body && <ChevronRightIcon sx={{ color: 'primary.main' }} />}
                                        </ListItemButton>
                                    </ListItem>
                                    {index < settingTabs.length - 1 && (
                                        <Divider
                                            variant='middle'
                                            component='li'
                                            sx={{
                                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </List>
                </Box>
            </Slide>

            <Slide direction='left' in={!!selectedTab} timeout={300} mountOnEnter unmountOnExit>
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.5,
                            minHeight: 48,
                        }}>
                        <IconButton onClick={handleBack} size='small'>
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{currentTab?.body}</Box>
                </Box>
            </Slide>
        </Box>
    );
}

export { SettingAccount };
